import React, { useContext } from "react";
import { useNavigate } from "react-router";
import { AuthContext, IntlContext } from "context";
import Image from "components/File/Image";
import type { ThreadInterface } from "types/thread";
import type { MessageUser } from "../types";
import { buildRoute, ROUTES } from "routes";
import { ArrowLeft, Info } from "lucide-react";
import { VerifiedProfileBadge } from "components/provider/VerifiedProfileBadge";
interface Props {
    checkIdentityOfUser: MessageUser | null;
    data: ThreadInterface;
    isInterlocutorProvider: boolean;
    isOnline: boolean;
    isHeaderMenuOpen: boolean;
    onToggleHeaderMenu: () => void;
}

const DiscussionTitle: React.FC<Props> = ({
    checkIdentityOfUser,
    data,
    isInterlocutorProvider,
    isOnline,
    isHeaderMenuOpen,
    onToggleHeaderMenu,
}) => {
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);
    const navigate = useNavigate();

    const isVerified = isInterlocutorProvider
        ? data.service?.provider?.dataleon_status === "approved"
        : data.client?.dataleon_status === "approved" || checkIdentityOfUser?.dataleon_status === "approved";

    const photoId = checkIdentityOfUser?.picture_id ?? checkIdentityOfUser?.photo ?? null;
    const initials = checkIdentityOfUser
        ? `${checkIdentityOfUser.first_name.charAt(0)}${checkIdentityOfUser.last_name.charAt(0)}`
        : "?";

    const canGoToProfile = isInterlocutorProvider && Boolean(data.service?.id);

    const handleGoToProfile = () => {
        if (canGoToProfile) {
            navigate(buildRoute.provider(data.service!.id));
        }
    };

    const avatarContent = photoId ? (
        <Image id={photoId} className="w-full h-full object-cover" />
    ) : (
        <span>{initials}</span>
    );

    return (
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-border">
            <button
                type="button"
                onClick={() => navigate(ROUTES.MESSAGING)}
                className="md:hidden flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary transition-colors"
                aria-label={intl.formatMessage("booking.messages.label.back_to_list")}
            >
                <ArrowLeft className="w-4 h-4" />
            </button>

            <button
                type="button"
                onClick={handleGoToProfile}
                disabled={!canGoToProfile}
                className="relative flex-shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
                aria-label={
                    canGoToProfile ? intl.formatMessage("booking.messages.label.view_profile") : undefined
                }
            >
                <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary flex items-center justify-center text-sm font-semibold text-secondary-foreground">
                    {avatarContent}
                </div>
                {isOnline && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                )}
            </button>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    {checkIdentityOfUser && (
                        <button
                            type="button"
                            onClick={handleGoToProfile}
                            disabled={!canGoToProfile}
                            className="font-semibold text-sm text-foreground truncate hover:underline text-left disabled:cursor-default disabled:no-underline"
                        >
                            {checkIdentityOfUser.first_name} {checkIdentityOfUser.last_name.charAt(0)}.
                        </button>
                    )}
                    {isVerified && <VerifiedProfileBadge />}
                </div>
                {isOnline && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-green-600 font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                        {intl.formatMessage("booking.messages.label.online")}
                    </span>
                )}
            </div>

            {data?.blockUser && account?.user.id !== data?.blockUser?.blocked_user_id && (
                <span className="text-xs text-destructive px-2 py-1 bg-destructive/10 rounded-md">
                    {intl.formatMessage("booking.messages.label.you_blocked_user")}
                </span>
            )}
            {data?.reportUser && account?.user.id === data?.reportUser?.user_id && (
                <span className="text-xs text-amber-700 px-2 py-1 bg-amber-50 rounded-md">
                    {intl.formatMessage("booking.messages.label.you_reported_user")}
                </span>
            )}

            <div className="relative flex-shrink-0">
                <button
                    type="button"
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        isHeaderMenuOpen
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-secondary"
                    }`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleHeaderMenu();
                    }}
                    title={intl.formatMessage("booking.messages.label.information")}
                    aria-expanded={isHeaderMenuOpen}
                    aria-haspopup="menu"
                >
                    <Info className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default DiscussionTitle;
